import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { DateTerm } from '../../../../utils/date'
import uiStates, {
  UiStateKey,
  UiStateScope,
} from '../../../../lib/commons/uiStates'
import DateVO from '../../../../vo/DateVO'
import { SearchUnit } from '../../../components/headers/HeaderBar/DateRangeSelector'
import { ROW_HEIGHT } from '../../../containers/BulkSheet'
import { DecimalAndSexagesimalScale } from '../../../../lib/functions/decimalAndSexagesimalScale'
import { EndDatePreset } from '../Header/EndDateSelector'
import { calculateEndDateFromPreset } from '../../TaskActualWorkApprovalStatus/hooks/pageState'

type StoredTaskActualWorkApprovalStatusPageState = {
  searchUnit: SearchUnit
  dateTerm: DateTerm
  rowHeight: ROW_HEIGHT
  decimalScaleUnit?: DecimalAndSexagesimalScale
  endDate?: string
  endDatePreset?: EndDatePreset
}

const checkIsCurrentMonth = (year: number, month: number): boolean => {
  const now = DateVO.now()
  return now.getYear() === year && now.getMonth() === month
}

export const usePageState = ({ functionUuid }: { functionUuid: string }) => {
  const now = DateVO.now()
  const [yyyy, mm] = now.getFirstDayOfMonth().format().split(/[-\/]/)
  const [yearMonthQuery, setYearMonthQuery] = useState(
    `year=${yyyy}&month=${mm}`
  )
  const [searchUnit, setSearchUnit] = useState<SearchUnit>(SearchUnit.MONTH)
  const [rowHeight, setRowHeight] = useState<ROW_HEIGHT>(ROW_HEIGHT.SMALL)
  const [decimalScaleUnit, setDecimalScaleUnit] =
    useState<DecimalAndSexagesimalScale>(DecimalAndSexagesimalScale.DECIMAL)
  const [endDate, setEndDate] = useState<DateVO>(now.getLastDayOfMonth())
  const [endDatePreset, setEndDatePreset] = useState<EndDatePreset>(
    EndDatePreset.END_OF_MONTH
  )
  const [initialized, setInitialized] = useState(false)
  const [displayDateTerm, setDisplayDateTerm] = useState<DateTerm>({
    startDate: now.getFirstDayOfMonth().format(),
    endDate: now.getLastDayOfMonth().format(),
  })
  const dateTerm = useRef<DateTerm>({
    startDate: now.getFirstDayOfMonth().format(),
    endDate: now.getLastDayOfMonth().format(),
  })
  const monthDateTerm = useRef<DateTerm>({
    startDate: now.getFirstDayOfMonth().format(),
    endDate: now.getLastDayOfMonth().format(),
  })

  const storePageState = useCallback(
    async (value: StoredTaskActualWorkApprovalStatusPageState) => {
      uiStates.update(
        {
          key: UiStateKey.PageState,
          scope: UiStateScope.User,
          value: JSON.stringify(value),
        },
        functionUuid
      )
    },
    [functionUuid]
  )

  useEffect(() => {
    if (!initialized) return
    const date = displayDateTerm.startDate
    if (!date) return
    const [yyyy, mm] = date.split(/[-\/]/)
    setYearMonthQuery(`year=${yyyy}&month=${mm}`)
    monthDateTerm.current = displayDateTerm
    dateTerm.current = displayDateTerm
  }, [initialized, displayDateTerm, searchUnit])

  useEffect(() => {
    if (!initialized) return
    setDisplayDateTerm(monthDateTerm.current)
    storePageState({
      searchUnit,
      dateTerm: dateTerm.current,
      rowHeight,
      decimalScaleUnit,
      endDate: endDate.serialize(),
      endDatePreset,
    })
  }, [
    initialized,
    storePageState,
    searchUnit,
    rowHeight,
    decimalScaleUnit,
    endDate,
    endDatePreset,
  ])

  const restorePageState = useCallback(async () => {
    const response = await uiStates.get({
      applicationFunctionUuid: functionUuid,
      key: UiStateKey.PageState,
      scope: UiStateScope.User,
    })
    const stored = response.json
    try {
      if (!stored.value) {
        return
      }
      const state = JSON.parse(stored.value)
      state.searchUnit && setSearchUnit(state.searchUnit)
      state.rowHeight && setRowHeight(state.rowHeight)
      state.decimalScaleUnit && setDecimalScaleUnit(state.decimalScaleUnit)

      const now = DateVO.now()
      if (state.dateTerm) {
        dateTerm.current = state.dateTerm
        monthDateTerm.current = state.dateTerm
        setDisplayDateTerm(state.dateTerm)

        const displayDate = new DateVO(state.dateTerm.startDate)
        const isCurrentMonth = checkIsCurrentMonth(
          displayDate.getYear(),
          displayDate.getMonth()
        )

        if (state.endDatePreset) {
          setEndDatePreset(state.endDatePreset)
        }

        if (isCurrentMonth) {
          if (state.endDate) {
            setEndDate(
              calculateEndDateFromPreset(
                state.endDatePreset,
                now.getLastDayOfMonth()
              )
            )
          } else {
            setEndDate(now.getLastDayOfMonth())
          }
        } else {
          const lastDayOfMonth = new DateVO(state.dateTerm.endDate)
          setEndDate(lastDayOfMonth)
        }
      } else {
        setEndDate(now.getLastDayOfMonth())
      }
    } finally {
      setInitialized(true)
    }
  }, [functionUuid])

  useEffect(() => {
    restorePageState()
  }, [restorePageState])

  const monthRange = useMemo(() => {
    if (!initialized) {
      const now = DateVO.now()
      return {
        startDateString: now.getFirstDayOfMonth().serialize(),
        endDateString: now.getLastDayOfMonth().serialize(),
      }
    }
    const startDate = new DateVO(displayDateTerm.startDate)
    const endDate = new DateVO(displayDateTerm.endDate)
    return {
      startDateString: startDate.serialize(),
      endDateString: endDate.serialize(),
    }
  }, [initialized, displayDateTerm])

  const handleMonthChange = useCallback(
    (year: number, month: number) => {
      const startDate = new DateVO(
        `${year}-${String(month).padStart(2, '0')}-01`
      )
      const nextMonth = month === 12 ? 1 : month + 1
      const nextYear = month === 12 ? year + 1 : year
      const nextMonthFirstDay = new DateVO(
        `${nextYear}-${String(nextMonth).padStart(2, '0')}-01`
      )
      const lastDayOfMonth = nextMonthFirstDay.addDays(-1)
      setDisplayDateTerm({
        startDate: startDate.serialize(),
        endDate: lastDayOfMonth.serialize(),
      })

      const isCurrentMonth = checkIsCurrentMonth(year, month)

      if (isCurrentMonth) {
        setEndDate(calculateEndDateFromPreset(endDatePreset, lastDayOfMonth))
      } else {
        setEndDate(lastDayOfMonth)
      }
    },
    [endDatePreset]
  )

  const isCurrentMonth = useMemo(() => {
    if (!initialized) return true
    const displayDate = new DateVO(displayDateTerm.startDate)
    return checkIsCurrentMonth(displayDate.getYear(), displayDate.getMonth())
  }, [initialized, displayDateTerm.startDate])

  return {
    initialized,
    searchUnit,
    rowHeight,
    setRowHeight,
    decimalScaleUnit,
    setDecimalScaleUnit,
    displayDateTerm,
    setDisplayDateTerm,
    monthRange,
    handleMonthChange,
    yearMonthQuery,
    endDate,
    setEndDate,
    isCurrentMonth,
    endDatePreset,
    setEndDatePreset,
  }
}
